import React, { useEffect, useMemo, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Tag,
  Tooltip,
  InputNumber,
  Badge,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  SortAscendingOutlined,
  StarFilled,
  CheckCircleFilled,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { packageService } from '../../services/packageService';
import { packageGroupService } from '../../services/packageGroupService';
import { PackageVO, PackageGroupVO, VipUnit } from '../../types';
import PackageGroupSelector from '../../components/PackageGroupSelector';
import {
  formatPackageDuration,
  LIFETIME_VIP_DURATION,
  resolvePackageDuration,
  VIP_DURATION_MAX,
} from '../../utils/packageDuration';
import {
  CONTINUOUS_PAY_EXTRA,
  CONTINUOUS_PAY_TOOLTIP,
  toContinuousPayFlag,
} from '../../utils/packageContinuousPay';
import {
  HIDE_AD_EXTRA,
  HIDE_AD_TOOLTIP,
  toHideAdFlag,
} from '../../utils/packageHideAd';

const { Option } = Select;
const { TextArea } = Input;

const groupTypeOptions = [
  { label: '充值套餐', value: 'PAY' },
  { label: '免费套餐', value: 'FREE' },
];

const sortPackageGroups = (groups: PackageGroupVO[]): PackageGroupVO[] => {
  return [...groups].sort((a, b) => {
    if (a.status !== b.status) {
      return b.status - a.status;
    }
    const timeA = a.createTime ? new Date(a.createTime).getTime() : 0;
    const timeB = b.createTime ? new Date(b.createTime).getTime() : 0;
    return timeB - timeA;
  });
};

const PackageList: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PackageVO[]>([]);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<PackageVO | null>(null);
  const [packageGroups, setPackageGroups] = useState<PackageGroupVO[]>([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [groupKeyword, setGroupKeyword] = useState('');
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const { user } = useAuth();

  const selectedGroup = packageGroups.find((group) => group.id === selectedGroupId) || null;
  const filteredPackageGroups = useMemo(() => {
    const keyword = groupKeyword.trim().toLowerCase();
    if (!keyword) {
      return packageGroups;
    }
    return packageGroups.filter((group) => {
      const typeLabel = groupTypeOptions.find((option) => option.value === group.groupType)?.label || '';
      return [group.groupName, group.deviceTypeName, typeLabel, group.groupType]
        .filter(Boolean)
        .some((text) => String(text).toLowerCase().includes(keyword));
    });
  }, [packageGroups, groupKeyword]);

  // 状态选项
  const statusOptions = [
    { label: '启用', value: 1 },
    { label: '禁用', value: 0 },
  ];

  // 获取套餐组列表
  const fetchPackageGroups = async () => {
    setGroupLoading(true);
    try {
      const params = {
        page: 0,
        size: 1000,
      };
      const response = await packageGroupService.queryPackageGroups(params);
      setPackageGroups(sortPackageGroups(response.content || []));
    } catch (error) {
      console.error('获取套餐组列表失败:', error);
      setPackageGroups([]);
    } finally {
      setGroupLoading(false);
    }
  };

  // 获取套餐列表
  const fetchData = async (
    page = current,
    size = pageSize,
    searchParams?: any,
    groupId = selectedGroupId,
  ) => {
    setLoading(true);
    try {
      // 如果没有传入searchParams，从searchForm获取当前的搜索条件
      const currentSearchParams = searchParams ?? searchForm.getFieldsValue();
      const params = {
        page: page - 1, // 后端从0开始
        size,
        ...currentSearchParams,
        ...(groupId != null ? { groupId } : {}),
      };
      
      const response = await packageService.queryPackages(params);
      // console.log('fetchData response:', response);
      setData(response.content);
      setTotal(response.totalElements); // 这里可能需要根据实际API调整
      setCurrent(page);
      setPageSize(size);
    } catch (error) {
      message.error('获取套餐列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackageGroups();
  }, []);

  useEffect(() => {
    fetchData(1, pageSize, undefined, selectedGroupId);
  }, [selectedGroupId]);

  // 搜索
  const handleSearch = () => {
    const values = searchForm.getFieldsValue();
    fetchData(1, pageSize, values);
  };

  // 重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    fetchData(1, pageSize, {}); // 传入空对象明确清除搜索条件
  };

  // 新增/编辑
  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({
      status: 1,
      sortOrder: 0,
      recommend: 0,
      defaultSelected: 0,
      continuousPay: 0,
      hideAd: 0,
      vipUnit: 'month',
      groupId: selectedGroup?.status === 1 ? selectedGroup.id : undefined,
    });
    setModalVisible(true);
  };

  // 解析配置JSON
  const parseConfig = (configStr: string | undefined): { monthAvg?: string } => {
    if (!configStr) return {};
    try {
      return JSON.parse(configStr);
    } catch {
      return {};
    }
  };

  // 构建配置JSON
  const buildConfig = (monthAvg?: string): string => {
    const config: { monthAvg?: string } = {};
    if (monthAvg) {
      config.monthAvg = monthAvg;
    }
    return Object.keys(config).length > 0 ? JSON.stringify(config) : '';
  };

  const handleEdit = (record: PackageVO) => {
    setEditingRecord(record);
    const configObj = parseConfig(record.config);
    const duration = resolvePackageDuration(record);
    form.setFieldsValue({
      packageName: record.packageName,
      groupId: record.groupId,
      originalPrice: record.originalPrice,
      actualPrice: record.actualPrice,
      vipDuration: duration.duration,
      vipUnit: duration.unit,
      status: record.status,
      sortOrder: record.sortOrder,
      description: record.description,
      configMonthAvg: configObj.monthAvg,
      remark: record.remark,
      recommend: record.recommend ?? 0,
      defaultSelected: record.defaultSelected ?? 0,
      continuousPay: toContinuousPayFlag(record.continuousPay),
      hideAd: toHideAdFlag(record.hideAd),
      discountType: record.discountType,
      discountValue: record.discountValue,
    });
    setModalVisible(true);
  };

  // 保存
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const createBy = user?.realName || user?.username || 'admin';
      
      // 将配置字段转换为JSON
      const { configMonthAvg, vipMonth: _vipMonth, ...restValues } = values;
      const submitValues = {
        ...restValues,
        config: buildConfig(configMonthAvg),
        continuousPay: toContinuousPayFlag(values.continuousPay),
        hideAd: toHideAdFlag(values.hideAd),
      };
      
      if (editingRecord) {
        await packageService.updatePackageWithDto(editingRecord.id, submitValues, createBy);
        message.success('套餐更新成功');
      } else {
        await packageService.createPackageWithDto(submitValues, createBy);
        message.success('套餐创建成功');
      }
      
      setModalVisible(false);
      fetchData();
    } catch (error) {
      message.error('操作失败');
    }
  };

  // 删除
  const handleDelete = async (record: PackageVO) => {
    try {
      const updateBy = user?.realName || user?.username || 'admin';
      await packageService.deletePackage(record.id, updateBy);
      message.success('删除成功');
      fetchData();
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 启用/禁用
  const handleToggleStatus = async (record: PackageVO) => {
    try {
      const updateBy = user?.realName || user?.username || 'admin';
      if (record.status === 1) {
        await packageService.disablePackage(record.id, updateBy);
        message.success('已禁用');
      } else {
        await packageService.enablePackage(record.id, updateBy);
        message.success('已启用');
      }
      fetchData();
    } catch (error) {
      message.error('操作失败');
    }
  };

  // 更新排序
  const handleUpdateSort = async (record: PackageVO, newSortOrder: number) => {
    try {
      const updateBy = user?.realName || user?.username || 'admin';
      await packageService.updatePackageSort(record.id, newSortOrder, updateBy);
      message.success('排序更新成功');
      fetchData();
    } catch (error) {
      message.error('排序更新失败');
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '套餐名称',
      dataIndex: 'packageName',
      key: 'packageName',
      width: 200,
      render: (value: string, record: PackageVO) => (
        <Space>
          <span>{value}</span>
          {record.recommend === 1 && (
            <Tag color="gold" icon={<StarFilled />}>推荐</Tag>
          )}
          {record.defaultSelected === 1 && (
            <Tag color="blue" icon={<CheckCircleFilled />}>默认</Tag>
          )}
          {toContinuousPayFlag(record.continuousPay) === 1 && (
            <Tooltip title={CONTINUOUS_PAY_TOOLTIP}>
              <Tag color="orange">连续支付</Tag>
            </Tooltip>
          )}
          {toHideAdFlag(record.hideAd) === 1 && (
            <Tooltip title={HIDE_AD_TOOLTIP}>
              <Tag color="purple">去广告</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: '套餐组',
      dataIndex: 'groupName',
      key: 'groupName',
      width: 120,
    },
    {
      title: '设备类型',
      dataIndex: 'deviceType',
      key: 'deviceType',
      width: 100,
    },
    {
      title: '价格',
      key: 'price',
      width: 120,
      render: (_: any, record: PackageVO) => (
        <div>
          <div style={{ textDecoration: 'line-through', color: '#999' }}>
            ¥{record.originalPrice}
          </div>
          <div style={{ color: '#f50', fontWeight: 'bold' }}>
            ¥{record.actualPrice}
          </div>
        </div>
      ),
    },
    {
      title: '会员有效期',
      key: 'vipDuration',
      width: 100,
      render: (_: unknown, record: PackageVO) => formatPackageDuration(record),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (value: number) => (
        <Tag color={value === 1 ? 'green' : 'red'}>
          {value === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '排序',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 80
    },
    {
      title: '帮他人购买折扣',
      key: 'discount',
      width: 130,
      render: (_: any, record: PackageVO) => {
        if (!record.discountType || !record.discountValue) {
          return <Tag color="default">无折扣</Tag>;
        }
        if (record.discountType === 1) {
          // 打折
          const discountText = record.discountValue >= 10 
            ? `${record.discountValue / 10}折` 
            : `${record.discountValue}折`;
          return <Tag color="orange">{discountText}</Tag>;
        } else if (record.discountType === 2) {
          // 一口价
          return <Tag color="purple">一口价¥{record.discountValue}</Tag>;
        }
        return <Tag color="default">无折扣</Tag>;
      },
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      ellipsis: true,
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 160,
      render: (value: string) => new Date(value).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: PackageVO) => (
        <Space size="small">
          <Tooltip title="编辑">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title={record.status === 1 ? '禁用' : '启用'}>
            <Switch 
              checked={record.status === 1} 
              size="small" 
              onChange={() => handleToggleStatus(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定要删除这个套餐吗？"
            onConfirm={() => handleDelete(record)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <Card
        title="套餐组"
        size="small"
        style={{ width: 280, flexShrink: 0 }}
        extra={<Button type="text" size="small" icon={<ReloadOutlined />} onClick={fetchPackageGroups} />}
      >
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          placeholder="搜索套餐组"
          value={groupKeyword}
          onChange={(event) => setGroupKeyword(event.target.value)}
          style={{ marginBottom: 8 }}
        />
        <div
          onClick={() => setSelectedGroupId(null)}
          style={{
            padding: '10px 12px',
            borderRadius: 8,
            border: selectedGroupId == null ? '1px solid #1677ff' : '1px solid #f0f0f0',
            background: selectedGroupId == null ? '#e6f4ff' : '#fff',
            cursor: 'pointer',
            marginBottom: 8,
          }}
        >
          <div style={{ fontWeight: 500 }}>全部</div>
          <div style={{ marginTop: 4, color: '#888', fontSize: 12 }}>显示全部套餐</div>
        </div>
        <Spin spinning={groupLoading}>
          <div
            className="package-group-filter-list"
            style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 'calc(100vh - 320px)', overflow: 'auto' }}
          >
            {filteredPackageGroups.map((group) => {
              const selected = selectedGroupId === group.id;
              const typeOption = groupTypeOptions.find((option) => option.value === group.groupType);
              return (
                <div
                  key={group.id}
                  onClick={() => setSelectedGroupId(group.id)}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: selected ? '1px solid #1677ff' : '1px solid #f0f0f0',
                    background: selected ? '#e6f4ff' : '#fff',
                    cursor: 'pointer',
                    opacity: group.status === 1 ? 1 : 0.72,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {group.groupName}
                    </span>
                    <Tag color={group.status === 1 ? 'green' : 'red'} style={{ marginInlineEnd: 0 }}>
                      {group.status === 1 ? '启用' : '禁用'}
                    </Tag>
                  </div>
                  <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <Tag color={group.groupType === 'PAY' ? 'blue' : 'green'} style={{ marginInlineEnd: 0 }}>
                      {typeOption?.label || group.groupType}
                    </Tag>
                    <span style={{ color: '#888', fontSize: 12 }}>{group.deviceTypeName || '-'}</span>
                  </div>
                </div>
              );
            })}
            {!groupLoading && packageGroups.length === 0 && (
              <div style={{ textAlign: 'center', color: '#999', padding: '24px 0' }}>暂无套餐组</div>
            )}
            {!groupLoading && packageGroups.length > 0 && filteredPackageGroups.length === 0 && (
              <div style={{ textAlign: 'center', color: '#999', padding: '24px 0' }}>未找到匹配的套餐组</div>
            )}
          </div>
        </Spin>
      </Card>

      <Card
        title={selectedGroup ? `套餐管理 · ${selectedGroup.groupName}` : '套餐管理'}
        style={{ flex: 1, minWidth: 0 }}
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => fetchData()}>
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              新增套餐
            </Button>
          </Space>
        }
      >
        {/* 搜索表单 */}
        <Form
          form={searchForm}
          layout="inline"
          style={{ marginBottom: 16 }}
          onFinish={handleSearch}
        >
          <Form.Item name="packageName" label="套餐名称">
            <Input placeholder="请输入套餐名称" allowClear />
          </Form.Item>
          {/* <Form.Item name="deviceType" label="设备类型">
            <Select placeholder="请选择设备类型" allowClear style={{ width: 120 }}>
              <Option value="4G">4G设备</Option>
              <Option value="5G">5G设备</Option>
              <Option value="WiFi">WiFi设备</Option>
            </Select>
          </Form.Item> */}
          {/* <Form.Item name="status" label="状态">
            <Select placeholder="请选择状态" allowClear style={{ width: 100 }}>
              {statusOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item> */}
          {/* <Form.Item name="minPrice" label="最低价格">
            <InputNumber placeholder="最低价格" style={{ width: 100 }} min={0} />
          </Form.Item>
          <Form.Item name="maxPrice" label="最高价格">
            <InputNumber placeholder="最高价格" style={{ width: 100 }} min={0} />
          </Form.Item> */}
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} htmlType="submit">
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 数据表格 */}
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            current,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
            onChange: (page, size) => fetchData(page, size),
            onShowSizeChange: (current, size) => fetchData(1, size),
          }}
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* 新增/编辑弹窗 */}
      <Modal
        title={editingRecord ? '编辑套餐' : '新增套餐'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        width={880}
        destroyOnHidden
        styles={{ body: { paddingTop: 12 } }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            status: 1,
            sortOrder: 0,
            recommend: 0,
            defaultSelected: 0,
            continuousPay: 0,
            hideAd: 0,
            vipUnit: 'month',
          }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="packageName"
                label="套餐名称"
                rules={[
                  { required: true, message: '请输入套餐名称' },
                  { max: 64, message: '套餐名称不能超过64个字符' },
                ]}
              >
                <Input placeholder="请输入套餐名称" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="groupId"
                label="套餐组"
                rules={[{ required: true, message: '请选择套餐组' }]}
              >
                <PackageGroupSelector style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="status"
                label="状态"
                rules={[{ required: true, message: '请选择状态' }]}
              >
                <Select>
                  {statusOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="originalPrice"
                label="原始价格"
                rules={[
                  { required: true, message: '请输入原始价格' },
                  { type: 'number', min: 0, message: '价格必须大于等于0' },
                ]}
              >
                <InputNumber
                  placeholder="请输入原始价格"
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  addonAfter="元"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="actualPrice"
                label="实际价格"
                rules={[
                  { required: true, message: '请输入实际价格' },
                  { type: 'number', min: 0, message: '价格必须大于等于0' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const originalPrice = getFieldValue('originalPrice');
                      if (!value || !originalPrice || value <= originalPrice) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('实际价格不能高于原始价格'));
                    },
                  }),
                ]}
              >
                <InputNumber
                  placeholder="请输入实际价格"
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  addonAfter="元"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.vipUnit !== currentValues.vipUnit}
              >
                {({ getFieldValue }) => {
                  const vipUnit = (getFieldValue('vipUnit') || 'month') as VipUnit;
                  const isLifetime = vipUnit === 'lifetime';
                  const maxDuration = VIP_DURATION_MAX[vipUnit];
                  const unitLabel = vipUnit === 'day' ? '天' : '月';
                  return (
                    <Form.Item
                      name="vipDuration"
                      label="会员有效期"
                      rules={[
                        { required: true, message: '请输入会员有效期' },
                        { type: 'number', min: 1, message: '会员有效期必须大于0' },
                        {
                          type: 'number',
                          max: maxDuration,
                          message: `会员有效期不能超过${maxDuration}${unitLabel}`,
                        },
                      ]}
                    >
                      <InputNumber
                        placeholder="请输入会员有效期"
                        style={{ width: '100%' }}
                        min={1}
                        max={maxDuration}
                        precision={0}
                        disabled={isLifetime}
                        formatter={isLifetime ? () => '终身' : undefined}
                        addonAfter={
                          <Form.Item name="vipUnit" noStyle rules={[{ required: true, message: '请选择有效期单位' }]}>
                            <Select
                              style={{ width: 80 }}
                              onChange={(nextUnit: VipUnit) => {
                                if (nextUnit === 'lifetime') {
                                  form.setFieldsValue({ vipDuration: LIFETIME_VIP_DURATION });
                                } else if (isLifetime) {
                                  form.setFieldsValue({ vipDuration: undefined });
                                }
                                form.validateFields(['vipDuration']);
                              }}
                            >
                              <Option value="month">月</Option>
                              <Option value="day">天</Option>
                              <Option value="lifetime">终身</Option>
                            </Select>
                          </Form.Item>
                        }
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="sortOrder"
                label="排序"
                rules={[
                  { required: true, message: '请输入排序值' },
                  { type: 'number', min: 0, message: '排序值必须大于等于0' },
                ]}
              >
                <InputNumber
                  placeholder="请输入排序值"
                  style={{ width: '100%' }}
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="recommend"
                label="推荐套餐"
                valuePropName="checked"
                getValueFromEvent={(checked: boolean) => checked ? 1 : 0}
                getValueProps={(value: number) => ({ checked: value === 1 })}
                tooltip="推荐套餐会在前端展示推荐标识"
              >
                <Switch checkedChildren="是" unCheckedChildren="否" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="defaultSelected"
                label="默认套餐"
                valuePropName="checked"
                getValueFromEvent={(checked: boolean) => checked ? 1 : 0}
                getValueProps={(value: number) => ({ checked: value === 1 })}
                tooltip="同一套餐组只允许一个默认套餐，设置后会自动取消其他套餐的默认状态"
              >
                <Switch checkedChildren="是" unCheckedChildren="否" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="continuousPay"
                label="连续支付"
                valuePropName="checked"
                getValueFromEvent={(checked: boolean) => (checked ? 1 : 0)}
                getValueProps={(value: number) => ({ checked: value === 1 })}
                tooltip={CONTINUOUS_PAY_TOOLTIP}
                extra={CONTINUOUS_PAY_EXTRA}
              >
                <Switch checkedChildren="开" unCheckedChildren="关" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="hideAd"
                label="去广告"
                valuePropName="checked"
                getValueFromEvent={(checked: boolean) => (checked ? 1 : 0)}
                getValueProps={(value: number) => ({ checked: value === 1 })}
                tooltip={HIDE_AD_TOOLTIP}
                extra={HIDE_AD_EXTRA}
              >
                <Switch checkedChildren="开" unCheckedChildren="关" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="description"
                label="描述"
                rules={[{ max: 500, message: '描述不能超过500个字符' }]}
              >
                <TextArea rows={2} placeholder="请输入描述" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="remark"
                label="备注"
                rules={[{ max: 255, message: '备注不能超过255个字符' }]}
              >
                <TextArea rows={2} placeholder="请输入备注" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <div style={{
                background: '#fafafa',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '16px',
                border: '1px solid #f0f0f0',
                height: '100%',
              }}>
                <div style={{
                  marginBottom: '12px',
                  fontWeight: 500,
                  color: '#333',
                  fontSize: '14px'
                }}>
                  套餐配置
                </div>
                <Form.Item
                  name="configMonthAvg"
                  label="月均价格文案"
                  tooltip="用于展示给用户的月均价格描述"
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder="请输入完整文案，例如：月均9.9元" />
                </Form.Item>
              </div>
            </Col>
            <Col span={12}>
              <div style={{
                background: '#fff7e6',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '16px',
                border: '1px solid #ffd591',
                height: '100%',
              }}>
                <div style={{
                  marginBottom: '12px',
                  fontWeight: 500,
                  color: '#d46b08',
                  fontSize: '14px'
                }}>
                  帮他人购买折扣（仅在为他人开通套餐时生效）
                </div>
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item
                      name="discountType"
                      label="折扣类型"
                      tooltip="设置后，用户为他人购买套餐时可享受此折扣"
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        placeholder="请选择折扣类型"
                        allowClear
                        onChange={() => {
                          form.setFieldValue('discountValue', undefined);
                        }}
                      >
                        <Option value={1}>打折</Option>
                        <Option value={2}>一口价</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.discountType !== currentValues.discountType ||
                        prevValues.originalPrice !== currentValues.originalPrice
                      }
                    >
                      {({ getFieldValue }) => {
                        const discountType = getFieldValue('discountType');
                        const originalPrice = getFieldValue('originalPrice');

                        if (discountType === 1) {
                          return (
                            <Form.Item
                              name="discountValue"
                              label="折扣值"
                              tooltip="输入折扣百分比，如80表示8折"
                              rules={[
                                { required: true, message: '请输入折扣值' },
                                { type: 'number', min: 1, max: 99, message: '折扣值必须在1-99之间' },
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <InputNumber
                                placeholder="如80表示8折"
                                style={{ width: '100%' }}
                                min={1}
                                max={99}
                                precision={0}
                                addonAfter="%"
                              />
                            </Form.Item>
                          );
                        } else if (discountType === 2) {
                          return (
                            <Form.Item
                              name="discountValue"
                              label="一口价"
                              tooltip="设置最终价格"
                              rules={[
                                { required: true, message: '请输入一口价' },
                                { type: 'number', min: 0.01, message: '一口价必须大于0' },
                                {
                                  validator: (_, value) => {
                                    if (value && originalPrice && value > originalPrice) {
                                      return Promise.reject(new Error('一口价不能高于原始价格'));
                                    }
                                    return Promise.resolve();
                                  },
                                },
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <InputNumber
                                placeholder="请输入最终价格"
                                style={{ width: '100%' }}
                                min={0.01}
                                max={originalPrice || undefined}
                                precision={2}
                                addonAfter="元"
                              />
                            </Form.Item>
                          );
                        }

                        return (
                          <Form.Item
                            label="折扣值"
                            style={{ marginBottom: 0 }}
                          >
                            <InputNumber
                              placeholder="请先选择折扣类型"
                              style={{ width: '100%' }}
                              disabled
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default PackageList; 